import { useEffect, useMemo, useState } from 'react'

import { logger } from '~/debug/logger'
import { usePrincipalOrgId } from '~/store/slices/currentOrg'

import type { BatteryModel } from '../client/ders'
import { useGetBatteriesV2Query } from '../client/ders'
import { combineSkip } from '../hooks/utils'
import type { IndexedDbService } from './storage/IndexedDbService'
import type { BatteryCacheRecord } from './storage/schema'

const BATTERIES_PAGE_SIZE = 1000

export type BatteriesDataManagerContextValue = {
  data: BatteryModel[]
  isLoading: boolean
  isFetching: boolean
}

function useBatteriesQuery(options?: { skip?: boolean }) {
  const principalOrgId = usePrincipalOrgId()

  const { data, isLoading, isFetching } = useGetBatteriesV2Query(
    {
      orgIds: principalOrgId ? [principalOrgId] : undefined,
      orgDepth: 100,
      includeLocalManaged: true,
      page: 0,
      count: BATTERIES_PAGE_SIZE
    },
    combineSkip(!principalOrgId, options)
  )

  return {
    batteries: data?.data,
    isLoading,
    isFetching
  }
}

export default function useBatteriesDataManager(service: IndexedDbService) {
  const { batteries, isLoading, isFetching } = useBatteriesQuery()

  const data = useBatteriesPersistence(service, batteries)

  return useMemo<BatteriesDataManagerContextValue>(
    () => ({
      data,
      isLoading,
      isFetching
    }),
    [data, isLoading, isFetching]
  )
}

function useBatteriesPersistence(service: IndexedDbService, batteries?: BatteryModel[]): BatteryModel[] {
  const [hydratedBatteries, setHydratedBatteries] = useState<BatteryModel[]>([])

  useEffect(() => {
    async function hydrateBatteriesCache() {
      try {
        const records = await service.getAll('batteries')
        setHydratedBatteries(records.map((record) => record.payload))
      } catch (error) {
        logger.error('Failed to hydrate batteries cache', error)
      }
    }

    void hydrateBatteriesCache()
  }, [service])

  useEffect(() => {
    if (!batteries) return

    const fetchedAt = Date.now()
    const records: BatteryCacheRecord[] = batteries.map((battery) => ({
      id: battery.id,
      payload: battery,
      syncedAt: fetchedAt
    }))

    const replaceRecords = async () => {
      try {
        await service.replace('batteries', records)
      } catch (error) {
        logger.error('Failed to persist batteries snapshot', error)
      }
    }

    void replaceRecords()
  }, [batteries, service])

  return batteries ?? hydratedBatteries
}
